import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import type { TradeApplicationDto, TradeApplicationStatus } from '@ecom/contracts';
import { useTradeApplicationsStore } from '@/stores/trade-applications.store';
import { useUiStore } from '@/i18n';
import { useT } from '@/i18n/use-t';
import type { MessageKey } from '@/i18n/messages';
import { date, dateTime, fileSize } from '@/lib/format';
import { mediaUrl } from '@/lib/media';
import { priceListOptions } from '@/lib/options';
import { AsyncSelect } from '@/components/async-select';
import { SearchIcon, UsersIcon } from '@/components/icons';
import {
  ListCard,
  ListToolbar,
  LoadMore,
  Page,
  Segmented,
  Sheet,
  TableScroll,
  Table,
  Tabs,
  Td,
  Th,
  Tr,
} from '@/components/page';
import { Alert, Button, EmptyState, Field, Input, Pill, TableSkeleton, Textarea, type PillTone } from '@/components/ui';

const STATUSES: TradeApplicationStatus[] = ['SUBMITTED', 'CHANGES_REQUESTED', 'APPROVED', 'REJECTED'];

const TONES: Record<TradeApplicationStatus, PillTone> = {
  SUBMITTED: 'warning',
  CHANGES_REQUESTED: 'info',
  APPROVED: 'success',
  REJECTED: 'danger',
};

const statusKey = (status: TradeApplicationStatus) => `tradeStatus${status}` as MessageKey;

/**
 * Trade applications (ADR-046, docs/01-Business/Trade.md): businesses asking
 * to buy at trade prices. Waiting ones first — they are the work — and each
 * answered in a panel beside the queue: onto a price list, back for changes,
 * or declined, with a note the applicant is mailed.
 */
export function TradeApplicationsPage() {
  const { items, loading, loadingMore, error, nextCursor, status, search, current, opening, load, loadMore, open, close } =
    useTradeApplicationsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [params, setParams] = useSearchParams();
  const [query, setQuery] = useState(search);

  useEffect(() => {
    void load();
  }, [load, locale]);

  // A link from a customer's page opens their application straight away.
  const linked = params.get('open');
  useEffect(() => {
    if (linked) void open(linked);
  }, [linked, open]);

  useEffect(() => {
    if (query === search) return;
    const timer = setTimeout(() => void load({ search: query }), 350);
    return () => clearTimeout(timer);
  }, [query, search, load]);

  const tabs: { id: TradeApplicationStatus | ''; label: string }[] = [
    ...STATUSES.map((value) => ({ id: value, label: t(statusKey(value)) })),
    { id: '', label: t('allStatuses') },
  ];

  return (
    <Page title={t('tradeApplications')} subtitle={t('tradeApplicationsSubtitle')} fill>
      <ListCard>
        <Tabs className="px-2" label={t('filterStatus')} value={status} onChange={(id) => void load({ status: id })} items={tabs} />
        <ListToolbar>
          <div className="relative w-full max-w-sm">
            <span className="pointer-events-none absolute inset-y-0 start-3 flex items-center text-ink-faint">
              <SearchIcon className="size-4" />
            </span>
            <Input
              type="search"
              placeholder={t('searchApplications')}
              aria-label={t('searchApplications')}
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              className="ps-9"
            />
          </div>
        </ListToolbar>

        {error ? (
          <div className="px-4 pt-4">
            <Alert>{error}</Alert>
          </div>
        ) : null}

        {loading ? (
          <TableSkeleton columns={5} />
        ) : items.length === 0 ? (
          <EmptyState title={t('noApplications')} body={t('noApplicationsHint')} icon={<UsersIcon />} />
        ) : (
          <TableScroll>
            <Table>
              <thead>
                <tr>
                  <Th>{t('application')}</Th>
                  <Th>{t('customer')}</Th>
                  <Th>{t('businessType')}</Th>
                  <Th>{t('received')}</Th>
                  <Th>{t('status')}</Th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => (
                  <Tr
                    key={row.id}
                    onClick={() => void open(row.id)}
                    className={current?.id === row.id ? 'bg-surface-sunken' : undefined}
                  >
                    <Td>
                      <span className="block font-medium text-ink">{row.tradeName || row.legalName}</span>
                      <span dir="ltr" className="block text-xs text-ink-muted">
                        {row.number}
                      </span>
                    </Td>
                    <Td className="text-ink">{row.customerName}</Td>
                    <Td>
                      <span className="block text-ink">{row.businessType.label}</span>
                      <span className="block text-xs text-ink-muted">{row.city}</span>
                    </Td>
                    <Td className="text-ink-muted">{date(row.createdAt, locale)}</Td>
                    <Td>
                      <Pill tone={TONES[row.status]}>{t(statusKey(row.status))}</Pill>
                    </Td>
                  </Tr>
                ))}
              </tbody>
            </Table>
          </TableScroll>
        )}

        <LoadMore visible={Boolean(nextCursor)} loading={loadingMore} onClick={() => void loadMore()} />
        {opening ? <p className="border-t border-line px-5 py-3 text-xs text-ink-muted">{t('loading')}</p> : null}
      </ListCard>

      {current ? (
        // Keyed, so a half-written answer never carries over to the next application.
        <ApplicationSheet
          key={current.id}
          application={current}
          onClose={() => {
            close();
            if (linked) setParams({});
          }}
        />
      ) : null}
    </Page>
  );
}

type Answer = 'approve' | 'changes' | 'reject';

/**
 * One application in a panel beside the queue: what they sent, and the store's
 * answer — or the three it can give, each of which mails the applicant, two of
 * them with a note it cannot do without.
 */
function ApplicationSheet({ application, onClose }: { application: TradeApplicationDto; onClose: () => void }) {
  const { deciding, decisionError, approve, requestChanges, reject } = useTradeApplicationsStore();
  const locale = useUiStore((state) => state.locale);
  const t = useT();
  const [answer, setAnswer] = useState<Answer>('approve');
  const [priceListId, setPriceListId] = useState('');
  const [note, setNote] = useState('');

  const waiting = application.status === 'SUBMITTED';
  const needsNote = answer !== 'approve';
  const ready = answer === 'approve' ? priceListId !== '' : note.trim() !== '';

  async function submit(event: FormEvent) {
    event.preventDefault();
    if (!ready) return;
    const done =
      answer === 'approve'
        ? await approve(application.id, priceListId, note)
        : answer === 'changes'
          ? await requestChanges(application.id, note)
          : await reject(application.id, note);
    if (done) setNote('');
  }

  const choices: { id: Answer; label: string }[] = [
    { id: 'approve', label: t('approveApplication') },
    { id: 'changes', label: t('requestChanges') },
    { id: 'reject', label: t('declineApplication') },
  ];

  return (
    <Sheet
      open
      size="lg"
      title={application.tradeName || application.legalName}
      subtitle={`${t('received')} ${dateTime(application.createdAt, locale)}`}
      onClose={onClose}
      footer={
        <>
          <Button variant="secondary" onClick={onClose}>
            {t('close')}
          </Button>
          {waiting ? (
            <Button
              type="submit"
              form="application-decision"
              variant={answer === 'reject' ? 'danger' : 'primary'}
              disabled={deciding || !ready}
            >
              {deciding ? t('saving') : choices.find((choice) => choice.id === answer)?.label}
            </Button>
          ) : null}
        </>
      }
    >
      <div className="flex flex-col gap-6">
        <p className="flex flex-wrap items-center gap-2 text-xs text-ink-muted">
          <Pill tone={TONES[application.status]}>{t(statusKey(application.status))}</Pill>
          <span dir="ltr">{application.number}</span>
          {application.customer ? (
            <>
              {' · '}
              <Link to={`/customers/${application.customer.id}`} className="text-brand-700 hover:underline">
                {application.customer.name || application.customer.email}
              </Link>
            </>
          ) : null}
        </p>

        <div className="grid gap-6 sm:grid-cols-2">
          <Facts title={t('company')}>
            <Fact label={t('legalName')}>{application.legalName}</Fact>
            {application.tradeName ? <Fact label={t('tradeName')}>{application.tradeName}</Fact> : null}
            <Fact label={t('businessType')}>{application.businessType.label}</Fact>
            <Fact label={t('city')}>{application.city}</Fact>
            <Fact label={t('crNumber')} numeric>{application.crNumber}</Fact>
            <Fact label={t('vatNumber')} numeric>{application.vatNumber ?? '—'}</Fact>
            {application.nationalAddress ? <Fact label={t('nationalAddress')}>{application.nationalAddress}</Fact> : null}
          </Facts>

          <Facts title={t('contact')}>
            <Fact label={t('fullName')}>{application.contact.name}</Fact>
            {application.contact.title ? <Fact label={t('jobTitle')}>{application.contact.title}</Fact> : null}
            <Fact label={t('mobile')} numeric>{application.contact.phone}</Fact>
            <Fact label={t('workEmail')} numeric>{application.contact.email}</Fact>
          </Facts>

          <Facts title={t('whatTheyBuy')}>
            <Fact label={t('monthlyVolume')}>{application.volume.label}</Fact>
            <Fact label={t('interests')}>
              {application.interests.length > 0
                ? application.interests.map((interest) => interest.name).join(locale === 'ar' ? '، ' : ', ')
                : '—'}
            </Fact>
            {application.notes ? <Fact label={t('applicantNotes')}>{application.notes}</Fact> : null}
          </Facts>
        </div>

        <section className="border-t border-line pt-5">
          <h3 className="text-sm font-semibold text-ink">{t('documents')}</h3>
          {/* Private files (ADR-044): each link lasts ten minutes, minted when this was opened. */}
          <p className="mt-0.5 text-xs text-ink-muted">{t('documentLinksExpire')}</p>
          {application.documents.length === 0 ? (
            <p className="mt-2 text-sm text-ink-muted">—</p>
          ) : (
            <ul className="mt-3 flex flex-col gap-2">
              {application.documents.map((document) => (
                <li key={document.id} className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm">
                  <span className="font-medium text-ink">{document.kindLabel}</span>
                  <a
                    href={mediaUrl(document.url)}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-brand-700 hover:underline"
                  >
                    {document.name}
                  </a>
                  <span data-numeric className="text-xs text-ink-muted">{fileSize(document.size, locale)}</span>
                </li>
              ))}
            </ul>
          )}
        </section>

        <section className="border-t border-line pt-5">
          {waiting ? (
            // The answer is sent from the panel's footer.
            <form id="application-decision" onSubmit={submit} className="flex flex-col gap-4">
              <div className="flex flex-wrap items-center justify-between gap-3">
                <h3 className="text-sm font-semibold text-ink">{t('decision')}</h3>
                <Segmented label={t('decision')} items={choices} value={answer} onChange={setAnswer} />
              </div>

              {answer === 'approve' ? (
                <Field label={t('approveOnto')} htmlFor="application-list" hint={t('approveOntoHint')}>
                  <AsyncSelect
                    id="application-list"
                    value={priceListId}
                    onChange={(id) => setPriceListId(id)}
                    fetchPage={priceListOptions.page}
                    fetchOne={priceListOptions.one}
                    placeholder={t('choosePriceList')}
                    allowClear={false}
                  />
                </Field>
              ) : null}

              <Field
                label={needsNote ? t('noteToApplicant') : t('noteToApplicantOptional')}
                htmlFor="application-note"
                hint={t('noteToApplicantHint')}
              >
                <Textarea
                  id="application-note"
                  value={note}
                  rows={3}
                  required={needsNote}
                  maxLength={2000}
                  onChange={(event) => setNote(event.target.value)}
                />
              </Field>

              {decisionError ? <Alert>{decisionError}</Alert> : null}
            </form>
          ) : (
            <div className="text-sm">
              <h3 className="font-semibold text-ink">{t('decision')}</h3>
              <p className="mt-1 text-ink-muted">
                {t(statusKey(application.status))} · {dateTime(application.decidedAt, locale)}
                {application.decidedBy ? ` · ${application.decidedBy}` : ''}
              </p>
              {application.priceList ? (
                <p className="mt-1 text-ink">
                  {t('priceList')}: {application.priceList.name}
                </p>
              ) : null}
              {application.decisionNote ? (
                <blockquote className="mt-2 whitespace-pre-line border-s-2 border-line ps-3 text-ink">{application.decisionNote}</blockquote>
              ) : null}
            </div>
          )}
        </section>
      </div>
    </Sheet>
  );
}

function Facts({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 text-sm font-semibold text-ink">{title}</h3>
      <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1.5 text-sm">{children}</dl>
    </section>
  );
}

function Fact({ label, numeric, children }: { label: string; numeric?: boolean; children: ReactNode }) {
  return (
    <>
      <dt className="text-ink-muted">{label}</dt>
      <dd dir={numeric ? 'ltr' : undefined} data-numeric={numeric ? '' : undefined} className="text-ink [overflow-wrap:anywhere]">
        {children}
      </dd>
    </>
  );
}
